import { loadFromStorage, removeFromStorage, saveToStorage } from './localStorage';

// ==========================================
// TYPES AND INTERFACES
// ==========================================

export interface MapState {
  center: { lat: number; lng: number };
  zoom: number | null;
  bounds?: MapBounds; // Optional bounds for better initial filtering
}

export interface MapBounds {
  neLat: number;
  neLng: number;
  swLat: number;
  swLng: number;
}

// ==========================================
// CONSTANTS
// ==========================================

const MAP_STATE_KEY = 'toptoptime_map_state';

// ==========================================
// DISTANCE AND COORDINATE CALCULATIONS
// ==========================================

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Whether a lat/lng pair is a genuine position. The API transforms default
 * missing coordinates to 0/0 (an online-only activity has none), so exact
 * (0,0) — Null Island — is treated as "no coordinates", as are non-finite or
 * missing values. Every map/pin consumer must gate on this, never on
 * truthiness (0 is falsy) or `!= null` (0 passes).
 */
export function hasRealCoordinates(latitude: number | null | undefined, longitude: number | null | undefined): boolean {
  if (latitude == null || longitude == null) return false;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  return !(latitude === 0 && longitude === 0);
}

/**
 * Calculates the distance between two points using the Haversine formula
 * NOTE: Duplicated in @toolkit/utils/location.utils.ts — keep in sync
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
}

/**
 * Calculates the clustering distance threshold in km based on zoom level
 * Lower zoom (zoomed out) = larger threshold (more generous clustering)
 * Higher zoom (zoomed in) = smaller threshold (tighter clustering)
 *
 * @param zoom - Zoom level (1-20)
 * @returns Distance threshold in kilometers, or 0 if zoom is invalid or too high
 */
export function calculateClusterThreshold(zoom: number | null | undefined): number {
  // If zoom is invalid, return 0 (no clustering)
  if (!zoom || zoom < 1 || zoom > 20) {
    return 0;
  }

  // Formula-based threshold derived from the actual pixel size of a map pin.
  //
  // At a given zoom level, one pixel represents:
  //   metersPerPixel = 156543 * cos(lat) / 2^zoom
  //
  // Using latitude ~45° (cos ≈ 0.707) as a mid-latitude reference:
  //   metersPerPixel ≈ 110700 / 2^zoom
  //
  // A pin is roughly 30px wide. Two pins overlap when their centers are
  // closer than ~30px apart. The threshold in km is:
  //   30 * metersPerPixel / 1000 = 3320 / 2^zoom
  //
  // We apply a 0.7 factor so clustering is not overly aggressive while still
  // preventing most visual overlap:
  //   threshold ≈ 2300 / 2^zoom  (km)
  const PIN_CLUSTER_CONSTANT = 2300;
  return PIN_CLUSTER_CONSTANT / Math.pow(2, zoom);
}

/**
 * Converts map bounds to center point and radius
 */
export function boundsToRadiusSearch(
  neLat: number,
  neLng: number,
  swLat: number,
  swLng: number
): {
  center: { lat: number; lng: number };
  radius: number;
} {
  // Calculate center point
  const centerLat = (neLat + swLat) / 2;
  const centerLng = (neLng + swLng) / 2;

  // Calculate radius (distance from center to a corner, e.g., northeast)
  // A more robust approach might be to calculate distance to all 4 corners and take the max,
  // or calculate the diagonal and divide by 2.
  // For simplicity, we'll use the distance to the NE corner.
  // A better radius would be to the furthest point on the bounding box from the center.
  const radiusNE = calculateDistance(centerLat, centerLng, neLat, neLng);
  const radiusSW = calculateDistance(centerLat, centerLng, swLat, swLng);
  const radiusNW = calculateDistance(centerLat, centerLng, neLat, swLng);
  const radiusSE = calculateDistance(centerLat, centerLng, swLat, neLng);

  // Use the maximum distance as radius
  const radius = Math.max(radiusNE, radiusSW, radiusNW, radiusSE);

  return {
    center: { lat: centerLat, lng: centerLng },
    radius: Math.ceil(radius) // Round up to ensure coverage
  };
}

/**
 * Calculate zoom level from radius
 */
export const calculateZoomFromRadius = (radiusInKm: number | null | undefined): number | null => {
  // Formula to convert radius to zoom level
  // zoom = log2(earth circumference / (radius * 2)) - 1
  if (!radiusInKm) return null;
  return Math.max(0, Math.min(20, Math.log2(40075 / (radiusInKm * 2)) - 1));
};

/**
 * Convert center point and radius to approximate bounds
 * This is useful when we have center/radius but need bounds format
 */
export const centerRadiusToBounds = (centerLat: number, centerLng: number, radiusKm: number): MapBounds => {
  // Approximate conversion: 1 degree ≈ 111 km at equator
  // This is a rough approximation and could be improved with more precise calculations
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos((centerLat * Math.PI) / 180));

  return {
    neLat: centerLat + latDelta,
    neLng: centerLng + lngDelta,
    swLat: centerLat - latDelta,
    swLng: centerLng - lngDelta
  };
};

/**
 * Calculate approximate bounds from center and zoom level
 * This approximates what Google Maps would show for a given viewport
 * Default viewport assumes map is 50% width (600px) of typical 1200px screen, full height (800px)
 *
 * @param centerLat - Center latitude
 * @param centerLng - Center longitude
 * @param zoom - Zoom level (1-20)
 * @param viewportWidth - Actual viewport width in pixels (default: 600 for 50% width map)
 * @param viewportHeight - Actual viewport height in pixels (default: 800)
 */
export const calculateBoundsFromCenterAndZoom = (
  centerLat: number,
  centerLng: number,
  zoom: number,
  viewportWidth: number = 600, // Default to 50% of typical 1200px screen
  viewportHeight: number = 800
): MapBounds => {
  // Calculate meters per pixel at given zoom level and latitude
  // Formula: metersPerPixel = (156543.03392 * cos(latitude)) / (2 ^ zoom)
  const metersPerPixel = (156543.03392 * Math.cos((centerLat * Math.PI) / 180)) / Math.pow(2, zoom);

  // Calculate the extent in meters
  const halfWidthMeters = (viewportWidth / 2) * metersPerPixel;
  const halfHeightMeters = (viewportHeight / 2) * metersPerPixel;

  // Convert meters to degrees (approximately 111,000 meters per degree)
  const latDelta = halfHeightMeters / 111000;
  const lngDelta = halfWidthMeters / (111000 * Math.cos((centerLat * Math.PI) / 180));

  // No padding factor - use actual calculated bounds based on viewport dimensions
  return {
    neLat: centerLat + latDelta,
    neLng: centerLng + lngDelta,
    swLat: centerLat - latDelta,
    swLng: centerLng - lngDelta
  };
};

// ==========================================
// BOUNDS AND FILTERING UTILITIES
// ==========================================

// ==========================================
// MAP STATE PERSISTENCE
// ==========================================

/**
 * Validator for MapState data structure
 */
const isValidMapState = (data: any): data is MapState => {
  const hasValidCenter =
    data &&
    typeof data === 'object' &&
    typeof data.center === 'object' &&
    typeof data.center.lat === 'number' &&
    typeof data.center.lng === 'number' &&
    !isNaN(data.center.lat) &&
    !isNaN(data.center.lng);

  if (!hasValidCenter) {
    return false;
  }

  const hasValidZoom =
    data.zoom === null || (typeof data.zoom === 'number' && !isNaN(data.zoom) && data.zoom >= 0 && data.zoom <= 20);

  if (!hasValidZoom) {
    return false;
  }

  // Validate bounds if present
  if (data.bounds !== undefined) {
    const bounds = data.bounds;
    const hasValidBounds =
      typeof bounds === 'object' &&
      typeof bounds.neLat === 'number' &&
      typeof bounds.neLng === 'number' &&
      typeof bounds.swLat === 'number' &&
      typeof bounds.swLng === 'number' &&
      !isNaN(bounds.neLat) &&
      !isNaN(bounds.neLng) &&
      !isNaN(bounds.swLat) &&
      !isNaN(bounds.swLng);

    if (!hasValidBounds) {
      return false;
    }
  }

  return true;
};

/**
 * Save map state to localStorage using the storage helper
 */
export const saveMapState = (mapState: MapState): boolean => {
  if (!isValidMapState(mapState)) {
    console.error('Invalid map state provided:', mapState);
    return false;
  }

  return saveToStorage(MAP_STATE_KEY, mapState);
};

/**
 * Load map state from localStorage using the storage helper
 */
export const loadMapState = (): MapState | null => {
  const result = loadFromStorage<MapState>(MAP_STATE_KEY, isValidMapState);

  if (result.success && result.data) {
    return result.data;
  }

  if (result.error && result.error !== 'No data found') {
    console.warn('Failed to load map state:', result.error);
  }

  return null;
};

/**
 * Clear saved map state using the storage helper
 */
export const clearMapState = (): boolean => {
  return removeFromStorage(MAP_STATE_KEY);
};

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

/**
 * Get default map state for a given location
 */
export const getDefaultMapState = (
  lat?: number | null,
  lng?: number | null,
  zoom?: number | null
): Partial<MapState> => {
  return {
    center: {
      lat: lat ?? 0,
      lng: lng ?? 0
    },
    zoom: zoom ?? null
  };
};

/**
 * Merge saved map state with fallback values
 */
export const getMergedMapState = (
  savedState: MapState | null,
  fallbackLat?: number | null,
  fallbackLng?: number | null,
  fallbackZoom?: number | null
): MapState => {
  if (savedState) {
    return {
      center: savedState.center,
      zoom: savedState.zoom
    };
  }

  return {
    center: {
      lat: fallbackLat ?? 0,
      lng: fallbackLng ?? 0
    },
    zoom: fallbackZoom ?? null
  };
};

/**
 * Calculate bounds from an array of markers
 * @param markers - Array of markers with lat and lng properties
 * @returns MapBounds that encompass all markers, or null if no valid markers
 */
export const calculateBoundsFromMarkers = (markers: Array<{ lat: number; lng: number }>): MapBounds | null => {
  if (!markers || markers.length === 0) {
    return null;
  }

  const validMarkers = markers.filter(
    (m) => typeof m.lat === 'number' && typeof m.lng === 'number' && !isNaN(m.lat) && !isNaN(m.lng)
  );

  if (validMarkers.length === 0) {
    return null;
  }

  // If only one marker, create a small bounds around it
  if (validMarkers.length === 1) {
    const marker = validMarkers[0];
    const padding = 0.01; // ~1km padding
    return {
      neLat: marker.lat + padding,
      neLng: marker.lng + padding,
      swLat: marker.lat - padding,
      swLng: marker.lng - padding
    };
  }

  // Calculate bounds from all markers
  let minLat = validMarkers[0].lat;
  let maxLat = validMarkers[0].lat;
  let minLng = validMarkers[0].lng;
  let maxLng = validMarkers[0].lng;

  for (const marker of validMarkers) {
    minLat = Math.min(minLat, marker.lat);
    maxLat = Math.max(maxLat, marker.lat);
    minLng = Math.min(minLng, marker.lng);
    maxLng = Math.max(maxLng, marker.lng);
  }

  // Add padding (10% of the range, with minimum padding)
  const latRange = maxLat - minLat;
  const lngRange = maxLng - minLng;
  const paddingLat = Math.max(latRange * 0.1, 0.01); // At least 0.01 degrees (~1km)
  const paddingLng = Math.max(lngRange * 0.1, 0.01);

  return {
    neLat: maxLat + paddingLat,
    neLng: maxLng + paddingLng,
    swLat: minLat - paddingLat,
    swLng: minLng - paddingLng
  };
};

/**
 * Calculate optimal zoom level to fit bounds within a viewport
 * This uses the inverse of the formula used in calculateBoundsFromCenterAndZoom
 * @param bounds - Map bounds to fit
 * @param viewportWidth - Viewport width in pixels (default: 400)
 * @param viewportHeight - Viewport height in pixels (default: 400)
 * @returns Zoom level (1-20) that fits the bounds, or null if bounds are invalid
 */
export const calculateZoomFromBounds = (
  bounds: MapBounds | null,
  viewportWidth: number = 400,
  viewportHeight: number = 400
): number | null => {
  if (!bounds) {
    return null;
  }

  const latRange = bounds.neLat - bounds.swLat;
  const lngRange = bounds.neLng - bounds.swLng;

  // Handle edge case where all markers are at the same point
  if (latRange === 0 && lngRange === 0) {
    return 15; // Default zoom for single point
  }

  // Calculate the center latitude for longitude calculations
  const centerLat = (bounds.neLat + bounds.swLat) / 2;

  // Convert lat/lng ranges to meters
  // 1 degree latitude ≈ 111,000 meters
  // 1 degree longitude ≈ 111,000 * cos(latitude) meters
  const latRangeMeters = latRange * 111000;
  const lngRangeMeters = lngRange * 111000 * Math.cos((centerLat * Math.PI) / 180);

  // Calculate required meters per pixel for both dimensions
  const requiredMetersPerPixelLat = latRangeMeters / viewportHeight;
  const requiredMetersPerPixelLng = lngRangeMeters / viewportWidth;

  // Use the larger value (more zoomed out) to ensure both dimensions fit
  const requiredMetersPerPixel = Math.max(requiredMetersPerPixelLat, requiredMetersPerPixelLng);

  // Reverse the formula from calculateBoundsFromCenterAndZoom:
  // metersPerPixel = (156543.03392 * cos(latitude)) / (2 ^ zoom)
  // Therefore: zoom = log2((156543.03392 * cos(latitude)) / metersPerPixel)
  const zoom = Math.log2((156543.03392 * Math.cos((centerLat * Math.PI) / 180)) / requiredMetersPerPixel);

  // Clamp zoom to valid range (1-20)
  const clampedZoom = Math.max(1, Math.min(20, Math.floor(zoom)));

  return clampedZoom;
};

/**
 * Calculate center and zoom from markers to fit them all in view
 * This is a convenience function that combines bounds and zoom calculation
 * @param markers - Array of markers with lat and lng properties
 * @param viewportWidth - Viewport width in pixels (default: 400)
 * @param viewportHeight - Viewport height in pixels (default: 400)
 * @returns Object with center and zoom, or null if no valid markers
 */
export const calculateMapViewFromMarkers = (
  markers: Array<{ lat: number; lng: number }>,
  viewportWidth: number = 400,
  viewportHeight: number = 400
): { center: { lat: number; lng: number }; zoom: number } | null => {
  const bounds = calculateBoundsFromMarkers(markers);
  if (!bounds) {
    return null;
  }

  const center = {
    lat: (bounds.neLat + bounds.swLat) / 2,
    lng: (bounds.neLng + bounds.swLng) / 2
  };

  const zoom = calculateZoomFromBounds(bounds, viewportWidth, viewportHeight);
  if (!zoom) {
    return null;
  }

  return { center, zoom };
};
